import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Money} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/money.types';
import {
  ConfettiAnimation,
  ContentPopper,
  ContentPopperProps,
  ModalDialogBodyContent,
  ModalDialogContent,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {memo} from 'react';
import {CouponDeepLinkActivationContent} from './CouponDeepLinkActivationContent';
import {CouponIcon} from './CouponIcon';

export type CouponDeepLinkActivationStatus =
  | 'success' // coupon is applied
  | 'loading'
  | 'error'
  | 'paused'; // coupons are paused but coupon is saved until the end of the campaign;

export interface CouponDeepLinkActivationProps {
  ['data-testid']?: string;
  className?: string;
  // if the coupon was already unlocked, the frontend shows a soft success UI in this case
  alreadyUnlocked?: boolean;
  coupon?: Coupon;
  // used on the basket coupon deeplink activation
  couponReduction?: Money;
  headline?: string;
  isOpen: boolean;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
  status: CouponDeepLinkActivationStatus;
  subline?: string;
}

const CouponDeepLinkActivationView: React.FC<CouponDeepLinkActivationProps> = ({
  'data-testid': dataTestId,
  alreadyUnlocked,
  coupon,
  couponReduction,
  headline,
  isOpen,
  LayoutComponent,
  status = 'loading',
  subline,
}) => {
  const showConfettiAnimation = status === 'success' && !alreadyUnlocked;

  return (
    <StyledContentPopper
      LayoutComponent={LayoutComponent}
      data-testid={dataTestId}
      aboveHeaderElement={
        <>
          {showConfettiAnimation && <FlyoutConfettiAnimation />}
          <StyledCouponIcon status={status} />
        </>
      }
      open={isOpen}
      onClose={() => {
        // do nothing: user can't manually close this
      }}
      contentFitWidth
      fixedSmallWidth={false}
      bodyContent={
        <CouponDeepLinkActivationContent
          status={status}
          headline={headline}
          subline={subline}
          coupon={coupon}
          couponReduction={couponReduction}
          ConfettiAnimationComponent={
            showConfettiAnimation && <ModalConfettiAnimation />
          }
        />
      }
    />
  );
};

const StyledContentPopper = styled(ContentPopper)`
  ${ModalDialogContent} {
    ${tabletAndUpLinaria(`
      max-width: 700px;
    `)}
  }

  ${ModalDialogBodyContent} {
    padding: 0;
  }
`;

const StyledCouponIcon = styled(CouponIcon)`
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateY(-50%);
  z-index: 1;

  ${tabletAndUpLinaria(`
    display: none;
  `)}
`;

const FlyoutConfettiAnimation = styled(ConfettiAnimation)`
  z-index: 9999;
  top: -20%;
  height: 120%;

  ${tabletAndUpLinaria(`
    display: none;
  `)}
`;

const ModalConfettiAnimation = styled(ConfettiAnimation)`
  display: none;

  ${tabletAndUpLinaria(`
    display: block;
    z-index: 1;
  `)}
`;

export const CouponDeepLinkActivation = memo(CouponDeepLinkActivationView);
